import { useMemo, useState } from 'react'
import { useReportBuilderStore } from '../stores/reportBuilderStore'
import { StepDataSelection } from './steps/StepDataSelection'
import { StepDistribution } from './steps/StepDistribution'
import { StepFilters } from './steps/StepFilters'
import { StepGrouping } from './steps/StepGrouping'
import { StepLabels } from './steps/StepLabels'
import { StepPreview } from './steps/StepPreview'

const STANDARD_STEPS = [
	{
		id: 1,
		name: 'Data Selection',
		short: 'Data',
		description: 'Select report type, forms and cycles',
	},
	{ id: 2, name: 'Grouping', short: 'Group', description: 'Configure grouping options' },
	{ id: 3, name: 'Filters', short: 'Filter', description: 'Apply filters' },
	{ id: 4, name: 'Labels', short: 'Label', description: 'Customize labels' },
	{ id: 5, name: 'Preview & Export', short: 'Preview', description: 'Preview and export report' },
]

const DISTRIBUTION_STEPS = [
	{ id: 1, name: 'Data Selection', short: 'Data', description: 'Select forms and cycles' },
	{ id: 2, name: 'Component', short: 'Comp', description: 'Select component to analyze' },
	{ id: 3, name: 'Filters', short: 'Filter', description: 'Apply filters' },
	{ id: 4, name: 'Labels', short: 'Label', description: 'Customize labels' },
	{ id: 5, name: 'Preview & Export', short: 'Preview', description: 'Preview and export report' },
]

type StepConfig = (typeof STANDARD_STEPS)[number]

export function ReportBuilderWizard() {
	const { currentStep, setCurrentStep, validationErrors, currentConfig, savedConfigs } = useReportBuilderStore()
	const [stepError, setStepError] = useState<string | null>(null)

	const isSaved = !!currentConfig.id

	const isDistributionMode = currentConfig.reportType === 'distribution'

	const STEPS: StepConfig[] = isDistributionMode ? DISTRIBUTION_STEPS : STANDARD_STEPS

	const hasUnsavedChanges = useMemo(() => {
		if (!currentConfig.id) return false
		const savedConfig = savedConfigs.find((c) => c.id === currentConfig.id)
		if (!savedConfig) return false
		return JSON.stringify(currentConfig) !== JSON.stringify(savedConfig)
	}, [currentConfig, savedConfigs])

	const renderStep = () => {
		switch (currentStep) {
			case 1:
				return <StepDataSelection />
			case 2:
				return isDistributionMode ? <StepDistribution /> : <StepGrouping />
			case 3:
				return <StepFilters />
			case 4:
				return <StepLabels />
			case 5:
				return <StepPreview />
			default:
				return <StepDataSelection />
		}
	}

	const validateCurrentStep = (): { valid: boolean; error?: string } => {
		switch (currentStep) {
			case 1:
				if (!currentConfig.reportType) {
					return { valid: false, error: 'Please select a report type' }
				}
				if (currentConfig.reportType === 'form' && currentConfig.selectedFormIds.length === 0) {
					return { valid: false, error: 'Please select at least one form' }
				}
				if (currentConfig.reportType === 'cycle' && currentConfig.selectedCycleIds.length === 0) {
					return { valid: false, error: 'Please select at least one cycle' }
				}
				if (currentConfig.reportType === 'cross-cycle' && currentConfig.selectedCycleIds.length < 2) {
					return {
						valid: false,
						error: 'Please select at least 2 cycles for cross-cycle comparison',
					}
				}
				if (
					currentConfig.reportType === 'raw-data' &&
					(!currentConfig.selectedRepeatableIds || currentConfig.selectedRepeatableIds.length === 0)
				) {
					return { valid: false, error: 'Please select at least one repeatable component' }
				}
				if (currentConfig.reportType === 'distribution' && currentConfig.selectedFormIds.length === 0) {
					return { valid: false, error: 'Please select at least one form' }
				}
				break
			case 2:
				if (isDistributionMode) {
					if (!currentConfig.distributionComponentId) {
						return { valid: false, error: 'Please select a component to analyze' }
					}
				} else if (currentConfig.reportType !== 'raw-data' && currentConfig.aggregateBy.length === 0) {
					return { valid: false, error: 'Please select at least one aggregation option' }
				}
				break
			case 4:
				if (!currentConfig.name.trim()) {
					return { valid: false, error: 'Please enter a report name' }
				}
				break
		}
		return { valid: true }
	}

	const handleStepClick = (stepId: number) => {
		if (!isSaved) return

		if (stepId > currentStep) {
			const validation = validateCurrentStep()
			if (!validation.valid) {
				setStepError(validation.error || 'Please complete the current step')
				return
			}
		}

		setStepError(null)
		setCurrentStep(stepId)
	}

	const getStepStatus = (stepId: number): 'completed' | 'current' | 'upcoming' => {
		if (stepId < currentStep) return 'completed'
		if (stepId === currentStep) return 'current'
		return 'upcoming'
	}

	return (
		<div className='bg-white border border-neutral-200 rounded-lg shadow-sm relative'>
			{isSaved && (
				<div className='absolute right-4 z-20' style={{ top: '-20px' }}>
					{hasUnsavedChanges ? (
						<span className='inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-neutral-100 text-neutral-600 border border-neutral-300 shadow-sm'>
							<svg className='h-3 w-3' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
								/>
							</svg>
							UNSAVED CHANGES
						</span>
					) : (
						<span className='inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300 shadow-sm'>
							<svg className='h-3 w-3' fill='currentColor' viewBox='0 0 20 20' aria-hidden='true'>
								<path
									fillRule='evenodd'
									d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
									clipRule='evenodd'
								/>
							</svg>
							SAVED
						</span>
					)}
				</div>
			)}
			<div className='border-b border-neutral-200 px-6 py-4'>
				<nav aria-label='Progress' className='max-w-4xl mx-auto'>
					<ol className='flex items-start justify-between'>
						{STEPS.map((step, stepIdx) => {
							const status = getStepStatus(step.id)
							return (
								<li key={step.id} className='flex-1 relative'>
									{stepIdx !== STEPS.length - 1 && (
										<div
											className='hidden sm:block absolute top-4 h-0.5 left-1/2 right-0 -translate-y-1/2'
											style={{ marginLeft: '20px', marginRight: '-50%' }}
											aria-hidden='true'>
											<div
												className={`h-full ${
													step.id < currentStep ? (isSaved ? 'bg-green-600' : 'bg-blue-600') : 'bg-neutral-200'
												}`}
											/>
										</div>
									)}
									<button
										type='button'
										onClick={() => handleStepClick(step.id)}
										disabled={!isSaved}
										aria-current={status === 'current' ? 'step' : undefined}
										aria-label={`Step ${step.id}: ${step.name} (${status})`}
										className={`group flex flex-col items-center relative z-10 w-full ${
											!isSaved ? 'cursor-default' : 'cursor-pointer'
										}`}
										title={!isSaved ? 'Save the report to navigate between steps' : undefined}>
										<span
											className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium ${
												step.id < currentStep
													? isSaved
														? 'bg-green-600 text-white'
														: 'bg-blue-600 text-white'
													: step.id === currentStep
														? isSaved
															? 'bg-white border-2 border-green-600 text-green-600'
															: 'bg-white border-2 border-blue-600 text-blue-600'
														: 'bg-white border-2 border-neutral-300 text-neutral-500'
											}`}>
											{step.id < currentStep ? (
												<svg className='h-5 w-5' viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
													<path
														fillRule='evenodd'
														d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
														clipRule='evenodd'
													/>
												</svg>
											) : (
												step.id
											)}
										</span>
										<span
											className={`mt-1 text-center whitespace-nowrap ${
												step.id <= currentStep ? (isSaved ? 'text-green-600' : 'text-blue-600') : 'text-neutral-500'
											}`}>
											<span className='block sm:hidden text-[10px] font-medium'>{step.short}</span>
											<span className='hidden sm:block text-xs font-medium'>{step.name}</span>
										</span>
									</button>
								</li>
							)
						})}
					</ol>
				</nav>
			</div>

			{(stepError || Object.keys(validationErrors).length > 0) && (
				<div className='mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md'>
					<ul className='text-sm text-red-700 list-disc list-inside'>
						{stepError && <li>{stepError}</li>}
						{Object.values(validationErrors).map((error) => (
							<li key={error}>{error}</li>
						))}
					</ul>
				</div>
			)}

			<div className='p-6'>{renderStep()}</div>
		</div>
	)
}
